import { BookIcon } from 'foundations-assets/icons/components/BookIcon';
import { LessonIcon } from 'foundations-assets/icons/components/LessonIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { BreakString } from 'foundations-components/transitional/components/BreakString';
import { Button } from 'foundations-components/transitional/components/Button';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { View } from 'foundations-components/transitional/components/View';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { AppReviewModal } from 'marketplace-ui-reviews/components/modals/AppReviewModal';
import { useReviewFlowContext } from 'marketplace-ui-reviews/contexts/ReviewFlowContext';
import { useCallback, useMemo, useState } from 'react';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { MediaCarousel } from '../../media/MediaCarousel';
import { WorkflowTemplatePreview } from '../../media/WorkflowTemplatePreview';
import { trackCustomMarketplaceOfferingBehavioralEvent } from '../../utils/TrackingUtils';
import { DetailsPageCard } from './DetailsPageCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const PRODUCT_OVERVIEW_CARD_SECTION = 'product overview card';
export function ProductOverviewCard({
  caseStudyUrl,
  isPreview,
  overview,
  reviewableId = 0,
  screenshots,
  setupDocsUrl,
  videos,
  enrollmentTrigger,
  firstAction,
  secondAction,
  totalActions,
  setupRequirements
}) {
  const {
    appContext,
    iconUrl,
    name,
    productType,
    runLeadGen,
    trackInteraction
  } = useDetailsPageContext();
  const isSolutionsPartnerProfile = productType === 'SOLUTIONS_PARTNER_PROFILE';
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const {
    editedReview,
    hasEditableReview,
    onReviewSubmitted,
    reviewabilityRequestStatus: reviewabilityRequestState,
    unreviewableReason
  } = useReviewFlowContext();

  // TODO: Use a product-type agnostic flag instead of checking for productType === 'APP'
  const showReviewCta = productType === 'APP' && appContext === 'app' && !isPreview && reviewabilityRequestState === 'SUCCEEDED' && (!unreviewableReason || hasEditableReview);
  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: PRODUCT_OVERVIEW_CARD_SECTION
    }, eventProps));
  }, [trackInteraction]);
  const mediaCarouselItems = useMemo(() => {
    const videoCarouselItems = (videos === null || videos === void 0 ? void 0 : videos.map(v => {
      var _v$demoVideoThumbnail;
      return {
        type: 'video',
        source: v.video.value,
        alt: v.video.altText || '',
        thumbnailUrl: (_v$demoVideoThumbnail = v.demoVideoThumbnail) === null || _v$demoVideoThumbnail === void 0 ? void 0 : _v$demoVideoThumbnail.value
      };
    })) || [];
    const screenshotCarouselItems = screenshots.map(s => ({
      type: 'image',
      source: s.screenshot.value,
      alt: s.screenshot.altText ? s.screenshot.altText : ''
    }));
    return [...videoCarouselItems, ...screenshotCarouselItems];
  }, [screenshots, videos]);
  const showButtonBar = useMemo(() => {
    return caseStudyUrl || setupDocsUrl;
  }, [caseStudyUrl, setupDocsUrl]);
  return /*#__PURE__*/_jsxs(DetailsPageCard, {
    name: "overview card",
    children: [showReviewCta && /*#__PURE__*/_jsx(View, {
      borderColor: "border-core-subtle",
      borderRadius: "200",
      borderStyle: "solid",
      borderWidth: "100",
      marginBlockEnd: "600",
      padding: "600",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "200",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h5",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.overviewCard.reviewCta.title",
            options: {
              name
            }
          })
        }), /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: hasEditableReview ? 'detailsPage.overviewCard.reviewCta.editBody' : 'detailsPage.overviewCard.reviewCta.body'
          })
        }), /*#__PURE__*/_jsx(Flex, {
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(Button, {
            onClick: () => setReviewModalOpen(true),
            size: "xs",
            use: "tertiary-light",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: hasEditableReview ? 'marketplace-ui-reviews.write.edit' : 'marketplace-ui-reviews.write.new'
            })
          })
        })]
      })
    }), /*#__PURE__*/_jsx(Flex, {
      paddingBlockEnd: "400",
      children: /*#__PURE__*/_jsx(Text, {
        as: "h4",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.overviewCard.title"
        })
      })
    }), productType === 'WORKFLOW_TEMPLATE' && enrollmentTrigger && /*#__PURE__*/_jsx(WorkflowTemplatePreview, {
      enrollmentTrigger: enrollmentTrigger,
      firstAction: firstAction,
      secondAction: secondAction,
      totalActions: totalActions
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "400",
      children: [mediaCarouselItems.length > 0 && /*#__PURE__*/_jsx(MediaCarousel, {
        onTrackInteraction: handleTrackInteraction,
        items: mediaCarouselItems,
        onItemClick: () => {
          handleTrackInteraction({
            action: 'click carousel image'
          });
          runLeadGen({
            action: 'CLICKED_IMAGE'
          });
        }
      }), /*#__PURE__*/_jsx(Flex, {
        "data-test-id": "expandable-overview",
        children: isSolutionsPartnerProfile ? /*#__PURE__*/_jsx(BreakString, {
          children: /*#__PURE__*/_jsx(Text, {
            as: "div",
            whiteSpace: "pre-wrap",
            children: /*#__PURE__*/_jsx(SanitizedText, {
              body: overview
            })
          })
        }) : /*#__PURE__*/_jsx(ExpandableText, {
          maxHeight: 280,
          hideButtonCaret: true,
          buttonAlign: "left",
          onExpandButtonClick: () => handleTrackInteraction({
            action: 'click expand overview'
          }),
          onCollapseButtonClick: () => handleTrackInteraction({
            action: 'click collapse overview'
          }),
          expandButtonText: /*#__PURE__*/_jsx(Text, {
            as: "span",
            color: "text-interactive-default",
            textDecoration: "underline",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.overviewCard.expandButtonText"
            })
          }),
          collapseButtonText: /*#__PURE__*/_jsx(Text, {
            as: "span",
            color: "text-interactive-default",
            textDecoration: "underline",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.overviewCard.collapseButtonText"
            })
          }),
          children: /*#__PURE__*/_jsx(BreakString, {
            children: /*#__PURE__*/_jsx(Text, {
              as: "div",
              whiteSpace: "pre-wrap",
              children: /*#__PURE__*/_jsx(SanitizedText, {
                body: overview
              })
            })
          })
        })
      }), setupRequirements && /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h6",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.overviewCard.setupRequirements.header"
          })
        }), /*#__PURE__*/_jsx(SanitizedText, {
          body: setupRequirements
        })]
      }), showButtonBar && /*#__PURE__*/_jsxs(Flex, {
        children: [caseStudyUrl && /*#__PURE__*/_jsxs(Button, {
          external: true,
          href: caseStudyUrl,
          onClick: () => {
            handleTrackInteraction({
              action: 'click case study'
            });
          },
          size: "xs",
          use: "tertiary-light",
          children: [/*#__PURE__*/_jsx(BookIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.overviewCard.caseStudy"
          })]
        }), setupDocsUrl && /*#__PURE__*/_jsxs(Button, {
          external: true,
          href: setupDocsUrl,
          onClick: () => {
            handleTrackInteraction({
              action: 'click setup guide'
            });
            trackCustomMarketplaceOfferingBehavioralEvent('SIDEBAR_SETUP_GUIDE');
            runLeadGen({
              action: 'CLICKED_SETUP_GUIDE'
            });
          },
          size: "xs",
          use: "tertiary-light",
          children: [/*#__PURE__*/_jsx(LessonIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.overviewCard.setupGuide"
          })]
        })]
      })]
    }), reviewModalOpen && /*#__PURE__*/_jsx(AppReviewModal, {
      appIconUrl: iconUrl,
      appName: name,
      editedReview: editedReview,
      isPublic: appContext === 'public',
      offeringId: reviewableId,
      onClose: () => setReviewModalOpen(false),
      onSuccess: () => {
        onReviewSubmitted();
        setReviewModalOpen(false);
      },
      onTrackInteraction: handleTrackInteraction,
      unreviewableReason: unreviewableReason
    })]
  });
}